import { CognitoAuth } from 'amazon-cognito-auth-js/dist/amazon-cognito-auth';
import {
  CognitoUserPool,
  CognitoRefreshToken,
  CognitoAccessToken,
  CognitoIdToken,
  CognitoUserSession,
} from 'amazon-cognito-identity-js';
import { config as AWSConfig } from 'aws-sdk';
import { getCognitoConfig, setAccessToken } from './utils';

const useCognitoUtils = () => {
  const cognitoConfig = JSON.parse(getCognitoConfig());

  AWSConfig.region = cognitoConfig.region;
  // Creates a CognitoAuth instance
  const createCognitoAuth = () => {
    const appWebDomain = cognitoConfig.userPoolBaseUri
      .replace('https://', '')
      .replace('http://', '');
    const auth = new CognitoAuth({
      UserPoolId: cognitoConfig.userPool,
      ClientId: cognitoConfig.clientId,
      AppWebDomain: appWebDomain,
      TokenScopesArray: cognitoConfig.tokenScopes,
      RedirectUriSignIn: cognitoConfig.callbackUri,
      RedirectUriSignOut: cognitoConfig.signoutUri,
    });
    return auth;
  };

  // Creates a CognitoUser instance
  const createCognitoUser = () => {
    const pool = createCognitoUserPool();
    return pool.getCurrentUser();
  };

  // Creates a CognitoUserPool instance
  const createCognitoUserPool = () =>
    new CognitoUserPool({
      UserPoolId: cognitoConfig.userPool,
      ClientId: cognitoConfig.clientId,
    });

  // Get the URI of the hosted sign in screen
  const getCognitoSignInUri = () => {
    const signinUri = `${cognitoConfig.userPoolBaseUri}/login?response_type=code&client_id=${cognitoConfig.clientId}&redirect_uri=${cognitoConfig.callbackUri}`;
    return signinUri;
  };

  // Parse the response from a Cognito callback URI (assumed a token or code is in the supplied href). Returns a promise.
  const parseCognitoWebResponse = (href) => {
    return new Promise((resolve, reject) => {
      const auth = createCognitoAuth();

      // userHandler will trigger the promise
      auth.userhandler = {
        onSuccess: function (result) {
          resolve(result);
        },
        onFailure: function (err) {
          reject(new Error('Failure parsing Cognito web response: ' + err));
        },
      };
      auth.parseCognitoWebResponse(href);
    });
  };

  // Gets a new Cognito session. Returns a promise.
  const getCognitoSession = () => {
    return new Promise((resolve, reject) => {
      const cognitoUser = createCognitoUser();
      cognitoUser.getSession((err, result) => {
        if (err || !result) {
          reject(new Error('Failure getting Cognito session: ' + err));
          return;
        }

        // Resolve the promise with the session credentials
        console.debug('Successfully got session: ' + JSON.stringify(result));
        const session = {
          credentials: {
            accessToken: result.accessToken.jwtToken,
            idToken: result.idToken.jwtToken,
            refreshToken: result.refreshToken.token,
            expiresIn: result.accessToken.payload.exp,
          },
          user: {
            userName: result.idToken.payload['cognito:username'],
            email: result.idToken.payload.email,
          },
        };
        resolve(session);
      });
    });
  };

  const getTokens = function (session) {
    return {
      accessToken: session.getAccessToken().getJwtToken(),
      idToken: session.getIdToken().getJwtToken(),
      refreshToken: session.getRefreshToken().getToken(),
    };
  };

  const getNewToken = (accessToken, idToken, refreshToken) => {
    return new Promise((resolve, reject) => {
      const AccessToken = new CognitoAccessToken({ AccessToken: accessToken });
      const IdToken = new CognitoIdToken({ IdToken: idToken });
      const RefreshToken = new CognitoRefreshToken({
        RefreshToken: refreshToken,
      });

      const cognitoUser = createCognitoUser();
      if (cognitoUser) {
        cognitoUser.refreshSession(RefreshToken, (err, session) => {
          const sessionData = {
            IdToken: IdToken,
            AccessToken: AccessToken,
            RefreshToken: RefreshToken,
          };
          const cachedSession = new CognitoUserSession(sessionData);

          if (err || !session) {
            reject(new Error('Cannot get access token: ' + err));
            return;
          }
          const tokens = getTokens(session);
          resolve(tokens);
        });
      } else {
        reject(new Error('Cannot get access token'));
      }
    });
  };

  // Sign out of the current session (will redirect to signout URI)
  const signOutCognitoSession = () => {
    setAccessToken('');
    const auth = createCognitoAuth();
    auth.signOut();
  };

  return {
    createCognitoAuth,
    createCognitoUser,
    createCognitoUserPool,
    getCognitoSession,
    getCognitoSignInUri,
    parseCognitoWebResponse,
    signOutCognitoSession,
    getNewToken,
  };
};

export default useCognitoUtils;
